import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';
import { orderService } from '../services/orderService';

const OrderContext = createContext();

export const useOrders = () => {
    const context = useContext(OrderContext);
    if (!context) throw new Error('useOrders must be used within an OrderProvider');
    return context;
};

export const OrderProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchOrders = useCallback(async () => {
        if (!isAuthenticated) {
            setOrders([]);
            return;
        }
        setLoading(true);
        try {
            const result = await orderService.getUserOrders();
            setOrders(result.data || []);
        } catch (error) {
            console.error('Errore nel caricamento ordini:', error);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const createOrder = async (orderData) => {
        setLoading(true);
        try {
            const result = await orderService.createOrder(orderData);
            setOrders(prev => [result.data, ...prev]);
            toast.success('Ordine creato con successo!');
            return { success: true, order: result.data };
        } catch (error) {
            const message = error?.message || 'Errore nella creazione ordine';
            toast.error(message);
            return { success: false, message };
        } finally {
            setLoading(false);
        }
    };

    const cancelOrder = async (orderId) => {
        try {
            await orderService.cancelOrder(orderId);
            setOrders(prev =>
                prev.map(o => o._id === orderId ? { ...o, status: 'cancelled' } : o)
            );
            toast.success('Ordine annullato');
            return { success: true };
        } catch (error) {
            const message = error?.message || 'Impossibile annullare l\'ordine';
            toast.error(message);
            return { success: false, message };
        }
    };

    const getOrderStats = useCallback(() => {
        const total = orders.length;
        const completed = orders.filter(o => o.status === 'delivered').length;
        const totalSpent = orders
            .filter(o => o.isPaid)
            .reduce((sum, o) => sum + (o.totalPrice || 0), 0);

        return {
            totalOrders: total,
            completedOrders: completed,
            totalSpent: totalSpent.toFixed(2),
            averageOrder: total > 0 ? (totalSpent / total).toFixed(2) : '0.00'
        };
    }, [orders]);

    // Recupera un singolo ordine dall'API (usato da OrderTracking).
    // Se l'id ha 24 caratteri esadecimali è un _id Mongo completo (utente loggato),
    // altrimenti viene trattato come codice breve (tracciamento pubblico).
    const getOrderTracking = useCallback(async (id) => {
        try {
            const isFullMongoId = /^[a-fA-F0-9]{24}$/.test(id);
            const result = isFullMongoId
                ? await orderService.getOrderById(id)
                : await orderService.getOrderByShortId(id);
            return result.data || null;
        } catch (error) {
            console.error('Errore nel recupero ordine:', error);
            return null;
        }
    }, []);

    // Aggiorna lo stato di un ordine (usato dall'admin)
    const updateOrderStatus = async (id, status) => {
        try {
            const result = await orderService.updateOrderStatus(id, status);
            setOrders(prev =>
                prev.map(o => o._id === id ? { ...o, status } : o)
            );
            toast.success('Stato ordine aggiornato');
            return { success: true, data: result.data };
        } catch (error) {
            const message = error?.message || 'Errore nell\'aggiornamento dello stato';
            toast.error(message);
            return { success: false, message };
        }
    };

    const value = {
        orders,
        loading,
        createOrder,
        cancelOrder,
        updateOrderStatus,
        refreshOrders: fetchOrders,
        getOrderById: (id) => orders.find(o => o._id === id),
        getOrderTracking,
        getOrderStats,
    };

    return (
        <OrderContext.Provider value={value}>
            {children}
        </OrderContext.Provider>
    );
};